import { FormikErrors } from 'formik';

export const OFFER_WIZARD_STEPS = [
  'Your Offer',
  'Upload Documents',
  'Terms',
  'Details',
] as const;

const TERMS_STEP = 2;
const DETAILS_STEP = 3;

/**
 * Every validated field, in the order it appears in the wizard, with the step
 * that renders it. The summary on the final step uses this to send the buyer
 * back to the right place instead of leaving Submit silently doing nothing.
 */
const FIELD_LOCATIONS: { field: string; label: string; step: number }[] = [
  { field: 'firstName', label: 'First Name', step: TERMS_STEP },
  { field: 'lastName', label: 'Last Name', step: TERMS_STEP },
  { field: 'email', label: 'Email', step: TERMS_STEP },
  { field: 'phone', label: 'Phone Number', step: TERMS_STEP },
  { field: 'paymentMethod', label: 'Payment Method', step: TERMS_STEP },
  { field: 'loan', label: 'Loan Amount', step: TERMS_STEP },
  { field: 'price', label: 'Offer Price', step: TERMS_STEP },
  { field: 'earnestMoney', label: 'Earnest Money', step: TERMS_STEP },
  { field: 'concessions', label: 'Concessions', step: TERMS_STEP },
  { field: 'closingDate', label: 'Closing Date', step: TERMS_STEP },
  {
    field: 'contingentPropertyAddress',
    label: 'Address of Home Being Sold',
    step: TERMS_STEP,
  },
  {
    field: 'contingentPropertyListed',
    label: 'Is Your Home Listed',
    step: TERMS_STEP,
  },
  {
    field: 'contingentPropertyUnderContract',
    label: 'Is Your Home Under Contract',
    step: TERMS_STEP,
  },
  {
    field: 'contingentPropertyClosingDate',
    label: 'Closing Date of Home Being Sold',
    step: TERMS_STEP,
  },
  {
    field: 'contingentPropertyListDate',
    label: 'List Date of Home Being Sold',
    step: TERMS_STEP,
  },
  { field: 'inspection', label: 'Inspection', step: DETAILS_STEP },
  { field: 'inspectionDays', label: 'Inspection Days', step: DETAILS_STEP },
  { field: 'homeWarrantyType', label: 'Home Warranty', step: DETAILS_STEP },
];

export interface OfferSubmitIssue {
  field: string;
  label: string;
  message: string;
  step: number;
  stepLabel: string;
}

export const getOfferSubmitIssues = (
  errors: FormikErrors<Record<string, unknown>>
): OfferSubmitIssue[] => {
  const known = FIELD_LOCATIONS.flatMap(({ field, label, step }) => {
    const message = errors[field];
    if (typeof message !== 'string' || !message) return [];
    return [
      { field, label, message, step, stepLabel: OFFER_WIZARD_STEPS[step] },
    ];
  });

  // A field added to the schema without a location still has to be reported,
  // otherwise the buyer is stuck again with nothing on screen.
  const unmapped = Object.entries(errors).flatMap(([field, message]) => {
    if (FIELD_LOCATIONS.some((location) => location.field === field)) return [];
    if (typeof message !== 'string' || !message) return [];
    return [
      {
        field,
        label: field,
        message,
        step: TERMS_STEP,
        stepLabel: OFFER_WIZARD_STEPS[TERMS_STEP],
      },
    ];
  });

  return [...known, ...unmapped];
};

export const getOfferSubmitErrorMessage = (error: unknown): string => {
  const serverMessage = (
    error as { response?: { data?: { message?: unknown } } }
  )?.response?.data?.message;
  if (typeof serverMessage === 'string' && serverMessage.trim()) {
    return serverMessage;
  }
  return 'We could not submit your offer. Please try again, or contact Homepoint if this keeps happening.';
};
